// ============================================================================
// VersionChecker — detecta nova versão e auto-recarrega (sem depender do SW)
// ============================================================================
// Por que existe: o Service Worker do PWA é notoriamente confiável-ish em
// detectar updates, especialmente em iOS Safari. Quando o SW falha em pegar
// nova versão, usuário fica preso com bundle antigo até limpar cache manual
// — o que é insustentável com 50 pessoas usando.
//
// Como funciona (independente do SW):
//   1. /api/version retorna o ID do deploy atual da Vercel (muda a cada deploy).
//   2. No boot do app, faz uma chamada inicial e guarda o ID em memória.
//   3. A cada 30s (e quando a tab volta a ficar visível), refaz a chamada.
//   4. Se o ID mudou → limpa caches + desregistra SW + reload silencioso.
//
// Vantagens vs /version.json estático:
//   - /api/* não é rewriteado pra index.html (a Vercel CDN gosta de cachear
//     a saída do rewrite e fica retornando HTML pra paths não-existentes).
//   - Endpoint serverless retorna sempre Cache-Control: no-store fresco.
//   - VERCEL_DEPLOYMENT_ID é único por deploy — sem precisar gerar arquivo.
//
// Resultado: usuário NUNCA precisa limpar cache. Update propaga em <60s
// pra todos os devices automaticamente.
// ============================================================================

import { useEffect, useRef } from 'react';
import {
  whenReloadAllowed, decidirRecarga, marcarVersaoPendente, quandoSoltarSemTeto, iniciarRecarga, registrarRecarregador,
} from '../utils/reloadHold.js';
import { CRM_SEM_RECARGA } from '../config/featureFlags.js';

// 60s entre polls. Combinado com s-maxage=60 do CDN, cada user faz 1 request/min,
// mas a função serverless só executa ~1× por minuto NO TOTAL (resto sai do cache
// edge). Antes era 30s e estourou o limite Hobby da Vercel.
const POLL_INTERVAL_MS = 60_000;
const ENDPOINT = '/api/version';

async function nukeCachesAndReload() {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
  } catch (e) {
    console.warn('[VersionChecker] falha ao limpar caches:', e);
  }
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    }
  } catch (e) {
    console.warn('[VersionChecker] falha ao desregistrar SW:', e);
  }
  // Reload com bust de query — garante que browser busca HTML novo
  const sep = window.location.search ? '&' : '?';
  window.location.href = `${window.location.pathname}${window.location.search}${sep}_v=${Date.now()}`;
}

async function fetchVersion() {
  try {
    const res = await fetch(`${ENDPOINT}?_=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('json')) return null; // Defensive: rewrite cacheado retornaria HTML
    const data = await res.json();
    return data?.buildTime || null;
  } catch {
    return null;
  }
}

export default function VersionChecker() {
  const knownVersionRef = useRef(null);
  const updatingRef = useRef(false);

  // o clique em "Atualizar" no aviso do CRM recarrega pelo mesmo caminho (limpa caches e SW)
  useEffect(() => (CRM_SEM_RECARGA ? registrarRecarregador(nukeCachesAndReload) : undefined), []);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (cancelled || updatingRef.current) return;
      const v = await fetchVersion();
      if (!v || cancelled) return;
      if (knownVersionRef.current == null) {
        knownVersionRef.current = v;
        return;
      }
      if (v !== knownVersionRef.current) {
        console.info(`[VersionChecker] nova versão detectada (${knownVersionRef.current} → ${v}) — recarregando…`);
        updatingRef.current = true;
        if (CRM_SEM_RECARGA) {
          // 27/10/2026: o ponto espera como sempre (45 s / 60 s). Com o CRM segurando
          // (texto, foco ou envio no campo de resposta), não recarrega: fica a
          // "versão nova pendente" e o aviso "Atualizar"; recarrega quando soltar.
          await whenReloadAllowed();
          if (decidirRecarga({ ligado: true }) === 'adiar') {
            marcarVersaoPendente('versao');
            await quandoSoltarSemTeto();
            await whenReloadAllowed();
          }
          if (!iniciarRecarga()) return; // o outro vigia (ou o clique no aviso) já recarregou
          await nukeCachesAndReload();
          return;
        }
        // Não recarrega no meio de uma batida de ponto (trava vence em 45 s;
        // a espera desiste em 60 s, então a atualização nunca fica presa).
        await whenReloadAllowed();
        await nukeCachesAndReload();
      }
    };

    // Boot: pega versão inicial (1s pra DOM assentar)
    const bootTimer = setTimeout(check, 1000);
    // Polling regular
    const pollTimer = setInterval(check, POLL_INTERVAL_MS);
    // Volta da aba (ex: abriu o app depois de 1 dia)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);
    // Reconectou
    const onOnline = () => check();
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      clearTimeout(bootTimer);
      clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  return null;
}
